"use client";

import Link from "next/link";
import { BlogCard } from "@/components/ui/BlogCard";
import { InfiniteScrollMarquee } from "@/components/ui/InfiniteScrollMarquee";
import { Reveal } from "@/components/ui/Reveal";
import type { BlogCategoryGroup, BlogPostMeta } from "@/data/blogs";

export type BlogsListingSectionProps = {
  categories: BlogCategoryGroup[];
  className?: string;
};

/** Shared diagonal tilt for every category row and its heading */
const TILT_DEG = 6;

function CategoryMarquee({
  category,
  posts,
  direction,
  isLast,
}: {
  category: string;
  posts: BlogPostMeta[];
  direction: "ltr" | "rtl";
  isLast?: boolean;
}) {
  return (
    <div
      className={[
        "mt-2 first:mt-0 sm:mt-3 lg:mt-4",
        isLast ? "pb-16 sm:pb-20 lg:pb-24" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <Reveal variant="up">
          <h2
            className="origin-center pb-2 pt-5 text-center font-heading text-3xl font-bold leading-none tracking-wide text-[#b46c29] will-change-transform sm:pb-2.5 sm:pt-6 sm:text-4xl lg:pb-3 lg:pt-7 lg:text-[45px] lg:leading-[54px]"
            style={{ transform: `rotate(${TILT_DEG}deg)` }}
          >
            {category}
          </h2>
        </Reveal>
      </div>

      {/* Real links for AT/keyboard — marquee DOM is duplicated and aria-hidden. */}
      <nav aria-label={`${category} blog posts`} className="sr-only">
        <ul>
          {posts.map((post) => (
            <li key={post.slug}>
              <Link href={`/blogs/${post.slug}`}>{post.title}</Link>
            </li>
          ))}
        </ul>
      </nav>

      <div className="relative mt-1 overflow-visible py-2 sm:mt-2 sm:py-3 lg:py-4">
        <div
          className="flex w-[135%] max-w-none origin-center -translate-x-[13%] flex-col gap-3 will-change-transform sm:w-[128%] sm:-translate-x-[11%] sm:gap-4"
          style={{ transform: `rotate(${TILT_DEG}deg)` }}
        >
          <span
            className="block h-0 w-full shrink-0 border-t-[3px] border-[#b46c29]"
            aria-hidden
          />
          <div aria-hidden="true">
            <InfiniteScrollMarquee
              direction={direction}
              baseSpeed={0}
              scrollInfluence={2.6}
            >
              {posts.map((post) => (
                <div
                  key={post.slug}
                  data-blog-slide
                  className="w-[min(82vw,280px)] shrink-0 sm:w-[min(46vw,300px)] lg:w-75 [&_img]:pointer-events-none"
                >
                  <BlogCard post={post} interactive={false} />
                </div>
              ))}
            </InfiniteScrollMarquee>
          </div>
          <span
            className="block h-0 w-full shrink-0 border-t-[3px] border-[#b46c29]"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

export function BlogsListingSection({
  categories,
  className,
}: BlogsListingSectionProps) {
  return (
    <section
      className={[
        "overflow-x-clip overflow-y-visible bg-white pb-10 pt-6 sm:pb-14 sm:pt-8 lg:pb-16 lg:pt-10",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {categories.map((group, index) => {
        const isEven = index % 2 === 0;
        const isLast = index === categories.length - 1;

        return (
          <CategoryMarquee
            key={group.category}
            category={group.category}
            posts={group.posts}
            direction={isEven ? "ltr" : "rtl"}
            isLast={isLast}
          />
        );
      })}
    </section>
  );
}
